import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
  DataAccessEventsService,
  DataAccessExhibitorService,
  DataAccessPostsService,
  DataAccessProductsService,
} from '@planone/bau365/data-access-fair-data';
import { mapApiResultsToFrontend as mapSearchResultsToFrontend } from '@planone/bau365/data-access-search';
import { UserHistoryEntry } from '@planone/bau365/data-access-user';
import { EntityType } from '@planone/bau365/util-fair-data';
import { catchError, defaultIfEmpty, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import {
  mapSearchToNewsfeedResult,
  mapUserHistoryEvent,
  mapUserHistoryExhibitor,
  mapUserHistoryPost,
  mapUserHistoryProduct,
  processError,
} from './data-access-newsfeed.utils';
import {
  ApiTopTagsResponse,
  NewsfeedExhibitorApiResponse,
  NewsfeedRootApiResponse,
  TopTag,
} from './newsfeed-api-response.models';

import { NEWSFEED_SERVICE_CONFIG, NewsfeedServiceConfigInterface } from '../config/newsfeed-service.config.interface';
import { Quicklink } from '../state/newsfeed-quicklinks/newsfeed-quicklinks.models';
import { NewsfeedResult } from '../state/newsfeed-results/newsfeed-results.models';
import { mapApiResultsToFrontend } from '../util/newsfeed-results.utils';

@Injectable()
export class NewsfeedService {
  constructor(
    private httpClient: HttpClient,
    private postsService: DataAccessPostsService,
    private productsService: DataAccessProductsService,
    private eventService: DataAccessEventsService,
    private exhibitorsService: DataAccessExhibitorService,
    @Inject(NEWSFEED_SERVICE_CONFIG) private config: NewsfeedServiceConfigInterface
  ) {}

  getRootChannelResults(
    from = 0,
    seed?: number
  ): Observable<{
    results: NewsfeedResult[];
    count: number;
    seed: number;
    from: number;
  }> {
    const size = from === 0 ? this.config.initialItemsCount : this.config.loadMoreItemsCount;
    const _seed = seed ?? new Date().getTime();
    const params = new HttpParams().appendAll({ from, size, seed: _seed });
    return this.httpClient.get<NewsfeedRootApiResponse>(`${this.config.newsfeedApiUrl}/newsfeed`, { params }).pipe(
      map((response) => ({
        results: mapSearchResultsToFrontend(response.results).map((apiResult) => mapSearchToNewsfeedResult(apiResult)),
        count: response.counts.all,
        seed: _seed,
        from,
      }))
    );
  }

  getSubscribedChannelResults(
    fairId: string,
    exhibitorId: string,
    cursor?: string,
    lastVisitedAt?: Date
  ): Observable<{
    results: NewsfeedResult[];
    nextCursor: string | undefined;
  }> {
    const limit = cursor === undefined ? this.config.initialItemsCount : this.config.loadMoreItemsCount;
    let params = new HttpParams().appendAll({ limit });
    if (cursor !== undefined) {
      params = params.appendAll({ cursor });
    }
    return this.httpClient
      .get<NewsfeedExhibitorApiResponse>(
        `${this.config.fairDataGatewayUrl}/news/channels/${fairId}/exhibitor/${exhibitorId}`,
        { params }
      )
      .pipe(
        map((response) => ({
          results: mapApiResultsToFrontend(response).map((apiResult) =>
            mapSearchToNewsfeedResult(apiResult, lastVisitedAt)
          ),
          nextCursor: response.cursors.next ?? undefined,
        }))
      );
  }

  getUserHistoryResults(entries: UserHistoryEntry[], from: number, count: number): Observable<NewsfeedResult[]> {
    return forkJoin(entries.slice(from, from + count).map((entry) => this.getEnrichedUserHistoryEntry(entry))).pipe(
      defaultIfEmpty([]),
      map((entries) => entries.filter((entry) => !!entry) as NewsfeedResult[])
    );
  }

  getEnrichedUserHistoryEntry(entry: UserHistoryEntry): Observable<NewsfeedResult | undefined> {
    const { id, fairId } = entry;
    switch (entry.entityType) {
      case EntityType.brand:
      case EntityType.exhibitor:
        return this.exhibitorsService.getExhibitor(fairId, id).pipe(
          map((exhibitor) => mapUserHistoryExhibitor(exhibitor)),
          catchError(processError)
        );
      case EntityType.event:
        return this.eventService.getEvent(fairId, id).pipe(
          switchMap((event) =>
            this.exhibitorsService.getExhibitor(fairId, event.exhibitor_id).pipe(
              map((exhibitor) => mapUserHistoryEvent(event, exhibitor)),
              catchError(processError)
            )
          ),
          catchError(processError)
        );
      case EntityType.post:
        return this.postsService.getPost(fairId, id).pipe(
          switchMap((post) =>
            this.exhibitorsService.getExhibitor(fairId, post.exhibitor_id).pipe(
              map((exhibitor) => mapUserHistoryPost(post, exhibitor)),
              catchError(processError)
            )
          ),
          catchError(processError)
        );
      case EntityType.product:
        return this.productsService.getProduct(fairId, id).pipe(
          switchMap((product) =>
            this.exhibitorsService.getExhibitor(fairId, product.exhibitor_id).pipe(
              map((exhibitor) => mapUserHistoryProduct(product, exhibitor)),
              catchError(processError)
            )
          ),
          catchError(processError)
        );
    }
  }

  getTopTags(fairId: string): Observable<{ topTags: TopTag[] }> {
    return this.httpClient.get<ApiTopTagsResponse>(`${this.config.quicklinksApiUrl}/fairs/${fairId}/top-tags`).pipe(
      map((response) => ({
        topTags: response.topics,
      }))
    );
  }

  getQuicklink(quicklink: TopTag, fairId: string): Observable<Quicklink | undefined> {
    return this.httpClient
      .get<{ id: string; name: string }>(`${this.config.fairDataGatewayUrl}/fairs/${fairId}/topics/${quicklink.id}`)
      .pipe(
        map((response) => ({
          name: response.name,
          id: response.id,
          filter: { topic: [response.id] },
        })),
        catchError(() => of(undefined))
      );
  }
}
